import { z } from "zod";
import {
  EMPLOYMENT_TYPES,
  EXPERIENCE_DURATIONS,
  SALARY_TYPES,
  VENUE_TYPES,
  WORKER_POSITIONS,
} from "../catalogs";
import {
  isoDateTimeString,
  latitudeSchema,
  longitudeSchema,
  uuidSchema,
} from "./common";

export const postingStateSchema = z.enum([
  "DRAFT",
  "OPEN",
  "CLOSED",
  "EXPIRED",
  "CANCELLED",
]);
export const salaryTypeSchema = z.enum(SALARY_TYPES);
export const employmentTypeSchema = z.enum(EMPLOYMENT_TYPES);
export const venueTypeSchema = z.enum(VENUE_TYPES);
export const workerPositionSchema = z.enum(WORKER_POSITIONS);
export const experienceDurationSchema = z.enum(EXPERIENCE_DURATIONS);
export const genderSchema = z.enum(["MALE", "FEMALE", "OTHER"]);

// VND amounts in requests: number or bigint. API converts to bigint before Prisma.
// Note: NOT using .transform() because zod-openapi doesn't support transforms in output schemas.
const salaryVndSchema = z.union([z.number().int().nonnegative(), z.bigint()]);
const salaryOutputSchema = z.union([z.string(), z.number(), z.bigint()]);

export const postingRequirementsSchema = z.object({
  gender: genderSchema.nullable().optional(),
  minAge: z.number().int().min(14).max(80).nullable().optional(),
  maxAge: z.number().int().min(14).max(80).nullable().optional(),
  minHeightCm: z.number().int().min(120).max(220).nullable().optional(),
  motorbikeRequired: z.boolean().optional(),
  excludeVisibleTattoo: z.boolean().optional(),
  kycCccdRequired: z.boolean().optional(),
  minExperience: experienceDurationSchema.nullable().optional(),
});

export const seasonalRangeSchema = z
  .object({
    startDate: isoDateTimeString,
    endDate: isoDateTimeString,
  })
  .refine((v) => new Date(v.endDate) > new Date(v.startDate), {
    message: "endDate must be after startDate",
    path: ["endDate"],
  });

export const preferredLanguageSchema = z.object({
  lang: z.string().min(2).max(8), // ISO 639-1 hoặc đơn vị "EN"/"KO"
  minLevel: z.enum(["BASIC", "INTERMEDIATE", "FLUENT"]),
});

// Each posting carries 1..30 work locations chosen from the org's saved
// address list. Detached snapshots live in the posting response under
// `addresses` (full list) and `nearestAddress` (server-computed for feed).
export const POSTING_ADDRESS_MIN = 1;
export const POSTING_ADDRESS_MAX = 30;

export const postingAddressIdsSchema = z
  .array(uuidSchema)
  .min(POSTING_ADDRESS_MIN, "Chọn ít nhất 1 địa điểm làm việc")
  .max(POSTING_ADDRESS_MAX, `Tối đa ${POSTING_ADDRESS_MAX} địa điểm`);

// Base object shared by the employer-facing create schema and the admin-only
// variant. Kept as a plain ZodObject (no `.refine`) so it can be `.extend`-ed.
const createPostingObjectSchema = z.object({
  position: workerPositionSchema,
  title: z.string().min(1).max(120),
  description: z.string().min(1).max(5000),
  venueType: venueTypeSchema.optional(), // default từ org.defaultVenueType
  employmentType: employmentTypeSchema,
  salaryMin: salaryVndSchema,
  salaryMax: salaryVndSchema,
  salaryType: salaryTypeSchema,
  headcount: z.number().int().min(1).max(50),
  benefits: z.array(z.string()).max(20).default([]),
  skillsRequired: z.array(z.string()).max(15).default([]),
  skillsPreferred: z.array(z.string()).max(15).default([]),
  requirements: postingRequirementsSchema.optional(),
  preferredLanguages: z.array(preferredLanguageSchema).max(5).optional(),
  softNotes: z.string().max(500).nullable().optional(),
  seasonalRange: seasonalRangeSchema.nullable().optional(),
  // Optional square feed-card image. R2 object key returned by the org image
  // presign (purpose: "posting"). Null/omit = fall back to org logo on feed.
  previewImageKey: z.string().max(255).nullable().optional(),
  addressIds: postingAddressIdsSchema,
});

export const createPostingRequestSchema = createPostingObjectSchema.refine(
  (v) => v.salaryMin <= v.salaryMax,
  { message: "salaryMin must be <= salaryMax", path: ["salaryMin"] },
);

// Admin-only create variant. Adds sourceUrl: the provenance link (e.g. a
// Facebook permalink) for a manually entered posting. Mirrors the bulk-import
// sourceUrl and is never exposed to employers (their schema omits this field).
export const adminCreatePostingRequestSchema = createPostingObjectSchema
  .extend({
    sourceUrl: z.string().url().max(2048).nullable().optional(),
  })
  .refine((v) => v.salaryMin <= v.salaryMax, {
    message: "salaryMin must be <= salaryMax",
    path: ["salaryMin"],
  });

export const updatePostingRequestSchema = z.object({
  title: z.string().min(1).max(120).optional(),
  description: z.string().min(1).max(5000).optional(),
  venueType: venueTypeSchema.optional(),
  salaryMin: salaryVndSchema.optional(),
  salaryMax: salaryVndSchema.optional(),
  salaryType: salaryTypeSchema.optional(),
  headcount: z.number().int().min(1).max(50).optional(),
  benefits: z.array(z.string()).max(20).optional(),
  skillsRequired: z.array(z.string()).max(15).optional(),
  skillsPreferred: z.array(z.string()).max(15).optional(),
  requirements: postingRequirementsSchema.optional(),
  preferredLanguages: z.array(preferredLanguageSchema).max(5).optional(),
  softNotes: z.string().max(500).nullable().optional(),
  seasonalRange: seasonalRangeSchema.nullable().optional(),
  // Square feed-card image. Pass a new R2 key to replace, null to clear, or
  // omit to leave the current image untouched.
  previewImageKey: z.string().max(255).nullable().optional(),
  // When omitted, addresses are left untouched. When supplied, the list is
  // replaced atomically (1..30). Detail of each OrgAddress is locked while
  // the posting is in OPEN state.
  addressIds: postingAddressIdsSchema.optional(),
});

// Admin-only update variant. Adds sourceUrl: the provenance link (e.g. a
// Facebook permalink). Pass null to clear, a string to set, or omit to leave
// untouched. Never exposed to employers (their schema omits this field).
export const adminUpdatePostingRequestSchema =
  updatePostingRequestSchema.extend({
    sourceUrl: z.string().url().max(2048).nullable().optional(),
  });

// Employer/admin-only constraints surfaced before opening the edit form.
// Lets the UI lock controls (disable removing a work location that already
// has interviews booked, floor the headcount at committed hires) instead of
// letting the user attempt an edit the API will reject.
export const postingEditConstraintsSchema = z.object({
  // Posting is only editable while OPEN. Mirrors the server guard so the UI
  // can render a read-only state for DRAFT/CLOSED/EXPIRED/CANCELLED.
  editable: z.boolean(),
  state: postingStateSchema,
  // orgAddressIds that have at least one active invite (LOCKED/ACCEPTED/
  // COMMITTED) anchored to them for this posting — cannot be detached.
  lockedAddressIds: z.array(uuidSchema),
  // Per-address active-invite count, keyed by orgAddressId. Drives the
  // "đang có N lịch phỏng vấn" hint next to a locked location.
  addressInterviewCounts: z.record(uuidSchema, z.number().int().nonnegative()),
  // COMMITTED invites for this posting = confirmed hires. Headcount cannot be
  // lowered below this number.
  committedHires: z.number().int().nonnegative(),
  // Any application exists (worker already applied). Position/employment type
  // are immutable regardless, but this lets the UI explain why.
  hasApplicants: z.boolean(),
});

export const duplicatePostingRequestSchema = z.object({
  sourcePostingId: uuidSchema,
});

export const postingFromTemplateRequestSchema = z.object({
  templateCode: z.string(),
  overrides: z
    .object({
      headcount: z.number().int().min(1).max(50).optional(),
      salaryMin: salaryVndSchema.optional(),
      salaryMax: salaryVndSchema.optional(),
      title: z.string().min(1).max(120).optional(),
      description: z.string().min(1).max(5000).optional(),
    })
    .optional(),
});

/** Org summary embedded in posting feed/detail. */
export const postingOrgSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  district: z.string(),
  city: z.string(),
  lat: z.number(),
  lng: z.number(),
  venueType: venueTypeSchema,
  bannerPublicUrl: z.string().nullable().optional(),
  logoPublicUrl: z.string().nullable().optional(),
  // Gallery photo URLs (public). Exposed so the feed card can fall back to the
  // first gallery image when a posting has no preview image and no org logo.
  galleryPublicUrls: z.array(z.string()).optional(),
  kycVerified: z.boolean(),
  // Deprecated (SCRUM-74) — kept one cycle for the feed badge.
  verifiedTier: z.enum(["NONE", "TRUSTED", "SUPER"]).optional(),
  // Verification ladder for the feed badge (SCRUM-74).
  verifyLevel: z
    .enum(["L0_NEW", "L1_CONTACT", "L2_DOCUMENT", "L3_TRUSTED"])
    .optional(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number().int().min(0),
});

export const matchBreakdownItemSchema = z.object({
  category: z.string(),
  hard: z.boolean(),
  pass: z.boolean(),
  label: z.string(),
  fixDeeplink: z.string().optional(),
});

// One work location attached to a posting. Reference into OrgAddress.
export const postingAddressSchema = z.object({
  id: uuidSchema,
  alias: z.string(),
  address: z.string(),
  district: z.string(),
  city: z.string(),
  lat: z.number(),
  lng: z.number(),
  // Source Google Maps URL of the branch — mobile prefers this when
  // opening Maps so workers see the branded place pin.
  mapUrl: z.string().url().nullable().optional(),
});

// Nearest-address summary on a feed card. Server picks the closest of the
// posting's attached addresses relative to the worker's GPS.
export const nearestAddressSchema = postingAddressSchema.extend({
  distanceKm: z.number().nullable(),
});

export const matchResultSchema = z.object({
  canCompute: z.boolean(),
  hardPass: z.boolean(),
  score: z.number().int().min(0).max(100),
  breakdown: z.array(matchBreakdownItemSchema),
});

export const postingSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  position: workerPositionSchema,
  title: z.string(),
  description: z.string(),
  venueType: venueTypeSchema,
  employmentType: employmentTypeSchema,
  salaryMin: salaryOutputSchema,
  salaryMax: salaryOutputSchema,
  salaryType: salaryTypeSchema,
  headcount: z.number().int(),
  benefits: z.array(z.string()),
  skillsRequired: z.array(z.string()),
  skillsPreferred: z.array(z.string()),
  requirements: postingRequirementsSchema,
  preferredLanguages: z.array(preferredLanguageSchema).nullable(),
  softNotes: z.string().nullable(),
  seasonalRange: seasonalRangeSchema.nullable(),
  // Optional square preview image (public URL). Highest-priority source for the
  // feed-card image — see resolvePostingCardImageUrl.
  previewImageUrl: z.string().nullable().optional(),
  state: postingStateSchema,
  createdAt: isoDateTimeString,
  publishedAt: isoDateTimeString.nullable().optional(),
  closedAt: isoDateTimeString.nullable().optional(),
  expiresAt: isoDateTimeString.nullable().optional(),
  // "Boost tin": when the posting is currently boosted, `isBoosted` is true and
  // `boostedUntil` is the ISO time the boost lapses. Drives the feed's boosted
  // tier + a "Được tài trợ" badge on the card.
  isBoosted: z.boolean().optional(),
  boostedUntil: isoDateTimeString.nullable().optional(),
  // 1..30 work locations. Worker mobile sorts by `nearestAddress` on feed
  // cards; detail screen renders the full list with map pins.
  addresses: z.array(postingAddressSchema),
  org: postingOrgSummarySchema,
  // Employer-list only: aggregate counts per posting so the list view can
  // surface "cần xử lý" without drilling into each posting.
  applicantStats: z
    .object({
      total: z.number().int().nonnegative(),
      pending: z.number().int().nonnegative(),
    })
    .optional(),
});

export const postingListResponseSchema = z.object({
  items: z.array(postingSchema),
  nextCursor: z.string().nullable(),
  // Tổng số tin khớp filter. Chỉ có khi client phân trang theo `page`
  // (web /viec-lam) — đường cursor (mobile feed) bỏ qua để khỏi COUNT thừa.
  total: z.number().int().nonnegative().nullish(),
});

// Express qs parses `?key=A` as string and `?key=A&key=B` as array.
// Wrap zod array schemas so a single value is accepted too.
const arrayable = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(
    (v) => (v === undefined || Array.isArray(v) ? v : [v]),
    z.array(item),
  );

export const postingFeedQuerySchema = z
  .object({
    // Filter chips
    position: arrayable(workerPositionSchema).optional(),
    employmentType: arrayable(employmentTypeSchema).optional(),
    venueType: arrayable(venueTypeSchema).optional(),
    benefits: arrayable(z.string()).optional(),
    skills: arrayable(z.string()).optional(),
    district: z.string().optional(),
    minSalary: salaryVndSchema.optional(),
    // Filter theo quán cụ thể
    orgId: uuidSchema.optional(),
    // Mặc định OPEN. Cho phép EXPIRED chỉ khi có search keyword `q`.
    state: postingStateSchema.optional(),
    // GPS
    lat: latitudeSchema.optional(),
    lng: longitudeSchema.optional(),
    maxKm: z.coerce.number().min(0.5).max(50).optional(),
    // Match toggle (default false — bật ON khi worker fill profile đủ).
    // API service converts string "true"/"false" → boolean.
    onlyEligible: z
      .union([z.boolean(), z.literal("true"), z.literal("false")])
      .optional(),
    // Sort
    sort: z
      .enum(["NEWEST", "NEAREST", "HIGHEST_SALARY", "BEST_MATCH"])
      .default("NEWEST"),
    // Search keyword
    q: z.string().max(100).optional(),
    // Pagination
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
  })
  .refine(
    (v) =>
      v.state === undefined ||
      v.state === "OPEN" ||
      (typeof v.q === "string" && v.q.trim().length > 0),
    {
      message: "Filter by non-OPEN state requires search keyword `q`",
      path: ["state"],
    },
  );

export const feedPostingCardSchema = postingSchema.extend({
  // Nearest of the posting's addresses to the worker's GPS — null when the
  // worker hasn't shared location. `distanceKm` mirrors what mobile renders
  // as the headline number on the card.
  nearestAddress: nearestAddressSchema.nullable().optional(),
  distanceKm: z.number().nullable().optional(),
  match: matchResultSchema.nullable().optional(),
});

export const postingFeedResponseSchema = z.object({
  items: z.array(feedPostingCardSchema),
  nextCursor: z.string().nullable(),
});

// Public, unauthenticated browse query (web /viec-lam). A worker-free subset of
// `postingFeedQuerySchema`: no GPS (lat/lng/maxKm), no match toggle
// (onlyEligible), and sort limited to the two orderings the public service can
// honour without a worker context. Response is `postingListResponseSchema`.
export const publicPostingFeedQuerySchema = z
  .object({
    position: arrayable(workerPositionSchema).optional(),
    employmentType: arrayable(employmentTypeSchema).optional(),
    venueType: arrayable(venueTypeSchema).optional(),
    benefits: arrayable(z.string()).optional(),
    skills: arrayable(z.string()).optional(),
    district: z.string().optional(),
    // Mã tỉnh/thành 2025 (VN_PROVINCES_2025, VD "79" = HCM). Lọc theo
    // OrgAddress.wardProvinceCode của các địa điểm gắn với tin.
    province: z.string().optional(),
    // Lọc lương theo range + đơn vị. `salaryType` giới hạn tin theo đúng đơn vị
    // người dùng chọn trên slider (Theo giờ = PER_HOUR, Theo tháng = PER_MONTH);
    // min/max là biên VND. Query params tới dạng string nên coerce sang number.
    minSalary: z.coerce.number().int().nonnegative().optional(),
    maxSalary: z.coerce.number().int().nonnegative().optional(),
    salaryType: salaryTypeSchema.optional(),
    orgId: uuidSchema.optional(),
    // Mặc định OPEN. Cho phép EXPIRED chỉ khi có search keyword `q`.
    state: postingStateSchema.optional(),
    sort: z.enum(["NEWEST", "HIGHEST_SALARY"]).default("NEWEST"),
    q: z.string().max(100).optional(),
    cursor: z.string().optional(),
    // Phân trang theo số trang (1-based) cho web. Loại trừ với `cursor`: có
    // `page` thì service chạy nhánh offset + COUNT và trả `total`.
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
  })
  .refine(
    (v) =>
      v.state === undefined ||
      v.state === "OPEN" ||
      (typeof v.q === "string" && v.q.trim().length > 0),
    {
      message: "Filter by non-OPEN state requires search keyword `q`",
      path: ["state"],
    },
  )
  .refine((v) => !(v.cursor !== undefined && v.page !== undefined), {
    message: "Use either `cursor` or `page`, not both",
    path: ["page"],
  });

export type PostingState = z.infer<typeof postingStateSchema>;
export type Posting = z.infer<typeof postingSchema>;
export type CreatePostingRequest = z.infer<typeof createPostingRequestSchema>;
export type AdminCreatePostingRequest = z.infer<
  typeof adminCreatePostingRequestSchema
>;
export type UpdatePostingRequest = z.infer<typeof updatePostingRequestSchema>;
export type AdminUpdatePostingRequest = z.infer<
  typeof adminUpdatePostingRequestSchema
>;
export type PostingEditConstraints = z.infer<
  typeof postingEditConstraintsSchema
>;
export type DuplicatePostingRequest = z.infer<
  typeof duplicatePostingRequestSchema
>;
export type PostingFromTemplateRequest = z.infer<
  typeof postingFromTemplateRequestSchema
>;
export type PostingListResponse = z.infer<typeof postingListResponseSchema>;
export type PostingFeedQuery = z.infer<typeof postingFeedQuerySchema>;
export type PublicPostingFeedQuery = z.infer<
  typeof publicPostingFeedQuerySchema
>;
export type PostingFeedResponse = z.infer<typeof postingFeedResponseSchema>;
export type FeedPostingCard = z.infer<typeof feedPostingCardSchema>;
export type PostingOrgSummary = z.infer<typeof postingOrgSummarySchema>;
export type PostingRequirements = z.infer<typeof postingRequirementsSchema>;
export type MatchResult = z.infer<typeof matchResultSchema>;
export type MatchBreakdownItem = z.infer<typeof matchBreakdownItemSchema>;
export type PreferredLanguage = z.infer<typeof preferredLanguageSchema>;
export type SeasonalRange = z.infer<typeof seasonalRangeSchema>;
export type PostingAddress = z.infer<typeof postingAddressSchema>;
export type NearestAddress = z.infer<typeof nearestAddressSchema>;

/**
 * Pick the feed-card image for a posting, in priority order:
 *   1. posting preview image (employer/admin-set)   → previewImageUrl
 *   2. org logo ("avatar")                           → org.logoPublicUrl
 *   3. first org gallery photo                       → org.galleryPublicUrls[0]
 *   4. org banner (legacy hero, kept so cards with only a banner don't regress)
 * Returns null when none exist — callers render their own default placeholder.
 */
export function resolvePostingCardImageUrl(posting: {
  previewImageUrl?: string | null;
  org: {
    logoPublicUrl?: string | null;
    galleryPublicUrls?: string[];
    bannerPublicUrl?: string | null;
  };
}): string | null {
  return (
    posting.previewImageUrl ||
    posting.org.logoPublicUrl ||
    posting.org.galleryPublicUrls?.[0] ||
    posting.org.bannerPublicUrl ||
    null
  );
}
